import { ChangeEvent, FC, Fragment, useCallback } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { debounce } from 'throttle-debounce';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useToggle from 'app/hooks/useToggle';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Models
import IContact from 'app/models/Contact';
// Types
import ContactRoles from 'app/types/ContactRoles';
// Async
import { getContactsAutocomplete } from 'app/store/Contacts/Contacts.async';
// Selectors
import { selectAutocompleteLoading, selectContactsAsOptions } from 'app/store/Contacts/Contacts.selectors';
// MUI
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
// Icons
import {
  Delete as DeleteIcon,
  VisibilityOutlined as VisibilityOutlinedIcon
} from '@mui/icons-material';
// Components
import { IconButton } from 'app/components/Mui';
import ContactDetailsDrawer from 'app/pages/Admin/ContactsPage/ContactDetailsDrawer';
// Utilities
import { isMaxLength, isRequired } from 'app/utilities/Validations';
import { camelCaseToNormalText, concatAddress } from 'app/utilities/Utilities';

interface IFormData {
  contacts: {
    contact: IContact | null;
    role: string;
  }[];
}

type Props = {
  disabled?: boolean;
  index: number;
  onRemove: (index: number) => void;
}

const CaseContactItem:FC<Props> = ({ disabled = false, index, onRemove }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const contactsOptions = useAppSelector(selectContactsAsOptions);
  const autocompleteLoading = useAppSelector(selectAutocompleteLoading);

  const { control, formState: { errors }, watch } = useFormContext<IFormData>();

  const contact = watch(`contacts.${index}.contact`);

  // eslint-disable-next-line
  const debounceContacts = useCallback(debounce(500, (search:string) => {
    dispatch(getContactsAutocomplete({ search, limit: 50 }));
  }), []);

  const handleChangeContactInput = (event:ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    debounceContacts(value);
  }

  const handleRemoveContact = (index: number) => () => {
    onRemove(index);
  }

  const { open, toggle } = useToggle();

  return (
    <Fragment>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mt: 4 }}>
        <Controller
          name={`contacts.${index}.contact`} control={control}
          rules={{ required: isRequired }}
          render={({ field: { value, onChange } }) => (
            <Autocomplete
              sx={{ flexGrow: 1 }}
              disabled={disabled}
              value={value as any}
              onChange={(_:any, nextValue:any) => onChange(nextValue)}
              options={contactsOptions || []}
              onClose={() => debounceContacts('')}
              renderOption={(props, option) => (
                <li {...props} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }} key={option.id}>
                  <Typography variant="subtitle1">{`${option.name}${option.props.referenceContact ? ` (${option.props.referenceContact.name})` : ''}`}</Typography>
                  {option.props.address ? (
                    <Typography variant="caption">{concatAddress(option.props.address)}</Typography>
                  ) : null}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  margin="none"
                  label={t('labels.contact')}
                  onChange={handleChangeContactInput}
                  required
                  error={Boolean(errors.contacts?.[index]?.contact)}
                  helperText={errors.contacts?.[index]?.contact?.message || ''}

                />
              )}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={autocompleteLoading}
            />
          )}
        />
        <Controller
          control={control} name={`contacts.${index}.role`}
          rules={{
            required: isRequired,
            maxLength: isMaxLength(100)
          }}
          render={({ field: { value, onChange } }) => (
            <Autocomplete
              sx={{ width: 180 }}
              freeSolo
              disabled={disabled}
              value={value ?? ''}
              onChange={(_, value) => onChange(value)}
              onInputChange={(_, value) => onChange(value)}
              options={(Object.keys(ContactRoles) as Array<keyof typeof ContactRoles>).map(key => camelCaseToNormalText(ContactRoles[key]))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  margin="none"
                  label={t('labels.role')}
                  required
                  error={Boolean(errors.contacts?.[index]?.role)}
                  helperText={errors.contacts?.[index]?.role?.message || ''}
                />
              )}
            />
          )}
        />
        <IconButton
          name={`Details contact ${index}`}
          onClick={toggle}
          sx={{ mt: 1 }}
          size="small"
          disabled={!contact}
        >
          <VisibilityOutlinedIcon />
        </IconButton>
        <IconButton
          name={`Delete contact ${index}`}
          onClick={handleRemoveContact(index)}
          sx={{ mt: 1 }}
          size="small"
          disabled={disabled}
        >
          <DeleteIcon />
        </IconButton>
      </Box>

      {open && contact ? (
        <ContactDetailsDrawer
          open={open}
          onClose={toggle}
          contactId={contact.id}
        />
      ) : null}
    </Fragment>
  );
};

export default CaseContactItem;
